import * as Toast from "@radix-ui/react-toast";
import {
	Building2,
	ChevronDown,
	ChevronRight,
	Edit,
	ExternalLink,
	Home,
	MapPin,
	Phone,
	Plus,
	Trash,
	User,
	X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Block } from "@/components/form-property/property-types";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { CreateBlockDialog } from "../Block/CreateBlockDialog";
import { AddPropertyToBlockDialog } from "./AddPropertyToBlockDialog";
import { CreateCondominiumDialog } from "./CondominiumCreateDialog";

interface Property {
	id: number;
	attributes: {
		propertyType: string;
		type: string;
		status: string;
		apartmentNumber: string;
		numberOfBedrooms: string;
		price: number;
		tagKanlo: string;
	};
}

interface CondominiumCardProps {
	id: number;
	name: string;
	region: string;
	street: string;
	neighborhood: string;
	number: string;
	administratorName?: string;
	commercialStatus: string;
	managerName?: string;
	managerPhone?: string;
	caretakerName?: string;
	caretakerPhone?: string;
	constructionYear?: string;
	zipCode?: string;
	builderName?: string;
	developerName?: string;
	numberOfServiceElevators?: string;
	numberOfPassengerElevators?: string;
	deliverySchedule?: string;
	corner?: boolean;
	guardhouse?: boolean;
	intercom?: boolean;
	terrace?: boolean;
	undergroundGarage?: boolean;
	beachService?: boolean;
	beachEquipmentStorage?: boolean;
	electronicGate?: boolean;
	groundFloorGarage?: boolean;
	collectiveGas?: boolean;
	onCondominiumUpdate: () => void;
}

export function CondominiumCard({
	id,
	name,
	region,
	street,
	neighborhood,
	number,
	commercialStatus,
	managerName,
	managerPhone,
	caretakerName,
	administratorName,
	caretakerPhone,
	constructionYear,
	zipCode,
	builderName,
	developerName,
	numberOfServiceElevators,
	numberOfPassengerElevators,
	deliverySchedule,
	corner,
	guardhouse,
	intercom,
	terrace,
	undergroundGarage,
	beachService,
	beachEquipmentStorage,
	electronicGate,
	groundFloorGarage,
	collectiveGas,
	onCondominiumUpdate,
}: CondominiumCardProps) {
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [openConfirm, setOpenConfirm] = useState(false);
	const [blocks, setBlocks] = useState<Block[]>([]);
	const [expandedBlocks, setExpandedBlocks] = useState<Record<number, boolean>>(
		{},
	);
	const [blockProperties, setBlockProperties] = useState<
		Record<number, Property[]>
	>({});
	const [toastOpen, setToastOpen] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const [toastType, setToastType] = useState<"success" | "error">("success");
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [wasEditing, setWasEditing] = useState(false);

	const route = import.meta.env.VITE_URL_ENDPOINT;
	const navigate = useNavigate();

	const showToast = (
		message: string,
		type: "success" | "error" = "success",
	) => {
		setToastMessage(message);
		setToastType(type);
		setToastOpen(true);
	};

	useEffect(() => {
		if (!editDialogOpen && wasEditing) {
			showToast("Condomínio atualizado com sucesso!");
			setWasEditing(false);
		}
	}, [editDialogOpen, wasEditing]);

	const fetchCondominiumDetails = async () => {
		if (expanded) {
			setExpanded(false);
			return;
		}

		setLoading(true);
		try {
			const response = await fetch(
				`${route}/api/blocks?filters[condominium][id][$eq]=${id}&populate=*&pagination[pageSize]=1000`,
			);
			const result = await response.json();
			setBlocks(result.data || []);
			setExpanded(true);
		} catch (error) {
			console.error("Erro ao buscar detalhes do condomínio:", error);
			showToast("Erro ao carregar detalhes do condomínio", "error");
		} finally {
			setLoading(false);
		}
	};

	const fetchBlockProperties = async (blockId: number) => {
		if (expandedBlocks[blockId]) {
			setExpandedBlocks((prev) => ({ ...prev, [blockId]: false }));
			return;
		}

		try {
			const response = await fetch(
				`${route}/api/properties?filters[block][id][$eq]=${blockId}&populate=*&pagination[pageSize]=1000`,
			);
			const result = await response.json();
			setBlockProperties((prev) => ({ ...prev, [blockId]: result.data || [] }));
			setExpandedBlocks((prev) => ({ ...prev, [blockId]: true }));
		} catch (error) {
			console.error("Erro ao buscar propriedades do bloco:", error);
			showToast("Erro ao carregar propriedades do bloco", "error");
		}
	};

	const handleDeleteCondominium = async () => {
		setDeleteLoading(true);
		try {
			const response = await fetch(`${route}/api/condominiums/${id}`, {
				method: "DELETE",
			});

			if (!response.ok) {
				throw new Error("Falha ao excluir condomínio");
			}

			showToast("Condomínio excluído com sucesso!");
			setOpenConfirm(false);
			onCondominiumUpdate();
		} catch (error) {
			console.error("Erro ao excluir condomínio:", error);
			showToast("Erro ao excluir condomínio", "error");
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleBlockCreate = (newBlock: Block) => {
		setBlocks((prev) => [...prev, newBlock]);
		showToast("Bloco criado com sucesso!");
	};

	const handleBlockUpdate = (updatedBlock: Block) => {
		setBlocks((prev) =>
			prev.map((block) =>
				block.id === updatedBlock.id ? updatedBlock : block,
			),
		);
		showToast("Bloco atualizado com sucesso!");
	};

	const handlePropertyAdded = (blockId: number) => {
		// Recarrega as propriedades do bloco após adicionar uma nova
		if (expandedBlocks[blockId]) {
			setExpandedBlocks((prev) => ({ ...prev, [blockId]: false }));
			setTimeout(() => fetchBlockProperties(blockId), 100);
		}
		showToast("Imóvel adicionado ao bloco com sucesso!");
	};

	const handleRemovePropertyFromBlock = async (
		propertyId: number,
		blockId: number,
	) => {
		try {
			// Remove a associação do imóvel com o bloco, definindo block como null
			const response = await fetch(`${route}/api/properties/${propertyId}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					data: {
						block: null,
					},
				}),
			});

			if (!response.ok) {
				throw new Error("Falha ao remover imóvel do bloco");
			}

			showToast("Imóvel removido do bloco com sucesso!");
			// Recarrega as propriedades do bloco
			fetchBlockProperties(blockId);
		} catch (error) {
			console.error("Erro ao remover imóvel do bloco:", error);
			showToast("Erro ao remover imóvel do bloco", "error");
		}
	};

	const handleNavigateToProperty = (propertyId: number) => {
		navigate(`/propriedade/${propertyId}`);
	};

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("pt-BR", {
			style: "currency",
			currency: "BRL",
		}).format(value);
	};

	return (
		<div className="w-full">
			<Toast.Provider swipeDirection="right">
				<div className="group mb-4 rounded-xl border border-gray-100 bg-white p-6 shadow-md transition-all duration-200 hover:shadow-lg">
					{/* Header do Condomínio */}
					<div className="flex flex-col items-start justify-between sm:flex-row sm:items-center">
						<div className="flex-1">
							<div className="mb-2 flex items-center gap-2">
								<Building2 className="text-blue-600" size={24} />
								<h2 className="text-xl font-bold text-gray-900">{name}</h2>
								<span
									className={`rounded-full px-2 py-1 text-xs font-medium ${
										commercialStatus === "Disponível"
											? "bg-green-100 text-green-800"
											: commercialStatus === "Vendido"
												? "bg-red-100 text-red-800"
												: "bg-yellow-100 text-yellow-800"
									}`}
								>
									{commercialStatus}
								</span>
							</div>

							<div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
								<span className="flex items-center gap-1">
									<MapPin size={16} className="text-gray-400" />
									{street}, {number} - {neighborhood}
								</span>
								<span className="font-medium text-blue-600">{region}</span>
							</div>

							<div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
								{managerName && (
									<span className="flex items-center gap-1">
										<User size={16} className="text-gray-400" />
										Síndico: {managerName}
									</span>
								)}
								{administratorName && (
									<span className="flex items-center gap-1">
										<Building2 size={16} className="text-gray-400" />
										Administradora: {administratorName}
									</span>
								)}
								{managerPhone && (
									<span className="flex items-center gap-1">
										<Phone size={16} className="text-gray-400" />
										{managerPhone}
									</span>
								)}
								{constructionYear && (
									<span className="text-gray-500">
										Construído em: {constructionYear}
									</span>
								)}
							</div>
						</div>

						<div className="mt-4 flex items-center gap-2 sm:mt-0">
							<Button
								onClick={fetchCondominiumDetails}
								className="rounded-full bg-blue-50 p-2 transition hover:bg-blue-100"
								title={expanded ? "Recolher detalhes" : "Ver detalhes"}
								disabled={loading}
							>
								{loading ? (
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
								) : expanded ? (
									<ChevronDown className="text-blue-600" size={20} />
								) : (
									<ChevronRight className="text-blue-600" size={20} />
								)}
							</Button>

							<CreateCondominiumDialog
								onCondominiumCreate={() => {
									setWasEditing(true);
									onCondominiumUpdate();
								}}
								open={editDialogOpen}
								onOpenChange={setEditDialogOpen}
								existingCondominium={{
									id,
									attributes: {
										Name: name,
										region,
										street,
										neighborhood,
										number,
										zipCode,
										commercialStatus,
										builderName,
										developerName,
										managerName,
										administratorName,
										managerPhone,
										caretakerName,
										caretakerPhone,
										constructionYear,
										numberOfServiceElevators,
										numberOfPassengerElevators,
										deliverySchedule,
										corner,
										guardhouse,
										intercom,
										terrace,
										undergroundGarage,
										beachService,
										beachEquipmentStorage,
										electronicGate,
										groundFloorGarage,
										collectiveGas,
									},
								}}
								trigger={
									<Button
										className="rounded-full bg-gray-100 p-2 transition hover:bg-gray-200"
										title="Editar condomínio"
									>
										<Edit className="fas fa-edit text-gray-700" />
									</Button>
								}
							/>

							<Dialog open={openConfirm} onOpenChange={setOpenConfirm}>
								<DialogTrigger asChild>
									<Button
										className="rounded-full bg-red-50 p-2 transition hover:bg-red-100"
										title="Excluir condomínio"
									>
										<Trash className="fas fa-trash text-red-600" />
									</Button>
								</DialogTrigger>
								<DialogContent className="rounded-xl bg-white p-4 shadow-lg sm:max-w-[425px]">
									<DialogHeader>
										<DialogTitle className="text-xl font-bold">
											Confirmar Exclusão
										</DialogTitle>
										<DialogDescription className="text-sm text-gray-500">
											Tem certeza que deseja excluir este condomínio? Esta ação
											não pode ser desfeita.
										</DialogDescription>
									</DialogHeader>
									<DialogFooter>
										<Button
											className="bg-red-600 text-white"
											onClick={handleDeleteCondominium}
											disabled={deleteLoading}
										>
											{deleteLoading ? "Excluindo..." : "Excluir"}
										</Button>
										<Button
											className="bg-gray-300"
											onClick={() => setOpenConfirm(false)}
											disabled={deleteLoading}
										>
											Cancelar
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>
						</div>
					</div>

					{/* Detalhes Expandidos */}
					{expanded && (
						<div className="mt-6 border-t border-gray-200 pt-6">
							<div className="mb-6">
								{/* Header dos Blocos com botão de adicionar */}
								<div className="mb-4 flex items-center justify-between">
									<h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
										<Building2 size={20} className="text-blue-600" />
										Blocos ({blocks.length})
									</h3>
									<CreateBlockDialog
										condominiumId={id}
										onBlockCreate={handleBlockCreate}
										trigger={
											<Button className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700">
												<Plus size={16} />
												Adicionar Bloco
											</Button>
										}
									/>
								</div>

								{/* Lista de Blocos */}
								{blocks.length > 0 ? (
									<div className="space-y-4">
										{blocks.map((block) => (
											<div
												key={block.id}
												className="rounded-lg border border-gray-200 bg-gray-50 p-4"
											>
												{/* Header do Bloco */}
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-3">
														<Button
															onClick={() => fetchBlockProperties(block.id)}
															className="rounded-full bg-blue-50 p-1 transition hover:bg-blue-100"
															title={
																expandedBlocks[block.id]
																	? "Recolher propriedades"
																	: "Ver propriedades"
															}
														>
															{expandedBlocks[block.id] ? (
																<ChevronDown
																	className="text-blue-600"
																	size={16}
																/>
															) : (
																<ChevronRight
																	className="text-blue-600"
																	size={16}
																/>
															)}
														</Button>
														<h4 className="font-medium text-gray-900">
															{block.attributes.name || `Bloco ${block.id}`}
														</h4>
													</div>

													<div className="flex items-center gap-2">
														<AddPropertyToBlockDialog
															blockId={block.id}
															condominiumId={id}
															blockName={
																block.attributes.name || `Bloco ${block.id}`
															}
															onPropertyAdded={() =>
																handlePropertyAdded(block.id)
															}
															trigger={
																<Button className="flex items-center gap-1 bg-green-600 text-white hover:bg-green-700">
																	<Plus size={14} />
																	Adicionar Imóvel
																</Button>
															}
														/>
														<CreateBlockDialog
															existingBlock={block}
															condominiumId={id}
															onBlockCreate={handleBlockUpdate}
															trigger={
																<Button className="rounded-full bg-gray-100 p-1 transition hover:bg-gray-200">
																	<Edit className="text-gray-700" size={14} />
																</Button>
															}
														/>
													</div>
												</div>

												{/* Informações do Bloco - ATUALIZADO PARA USAR OS CAMPOS CORRETOS */}
												<div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
													{block.attributes.numberOfFloors && (
														<span>
															Andares: {block.attributes.numberOfFloors}
														</span>
													)}
													{block.attributes.apartmentsPerFloor && (
														<span>
															Aptos/Andar: {block.attributes.apartmentsPerFloor}
														</span>
													)}
													{block.attributes.ConstructionPhase && (
														<span>
															Fase: {block.attributes.ConstructionPhase}
														</span>
													)}
													{block.attributes.FloorNumber && (
														<span>Andar: {block.attributes.FloorNumber}</span>
													)}
													{block.attributes.groundOrFloor && (
														<span>
															Térreo/Andar: {block.attributes.groundOrFloor}
														</span>
													)}
												</div>

												{/* Propriedades do Bloco */}
												{expandedBlocks[block.id] && (
													<div className="mt-4 border-t border-gray-300 pt-4">
														<h5 className="mb-3 flex items-center gap-2 font-medium text-gray-900">
															<Home size={16} className="text-green-600" />
															Imóveis ({blockProperties[block.id]?.length || 0})
														</h5>
														{blockProperties[block.id]?.length > 0 ? (
															<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
																{blockProperties[block.id].map((property) => (
																	<div
																		key={property.id}
																		className="relative rounded-lg border border-gray-100 bg-white p-3 shadow-sm"
																	>
																		{/* Botões de ação */}
																		<div className="absolute right-2 top-2 flex gap-1">
																			<Button
																				onClick={() =>
																					handleNavigateToProperty(property.id)
																				}
																				className="h-6 w-6 rounded-full bg-blue-50 p-0 transition hover:bg-blue-100"
																				title="Ver imóvel"
																			>
																				<ExternalLink
																					className="text-blue-600"
																					size={14}
																				/>
																			</Button>
																			<Button
																				onClick={() =>
																					handleRemovePropertyFromBlock(
																						property.id,
																						block.id,
																					)
																				}
																				className="h-6 w-6 rounded-full bg-red-50 p-0 transition hover:bg-red-100"
																				title="Remover do bloco"
																			>
																				<X className="text-red-600" size={14} />
																			</Button>
																		</div>

																		<div className="mb-2 flex items-start justify-between pr-16">
																			<h6 className="font-medium text-gray-900">
																				{property.attributes.type ||
																					property.attributes.propertyType}
																				{property.attributes.apartmentNumber &&
																					` - Apto ${property.attributes.apartmentNumber}`}
																			</h6>
																		</div>
																		<div className="mb-2">
																			<span
																				className={`rounded-full px-2 py-1 text-xs font-medium ${
																					property.attributes.status ===
																					"À venda"
																						? "bg-blue-100 text-blue-800"
																						: property.attributes.status ===
																								"Vendido"
																							? "bg-red-100 text-red-800"
																							: property.attributes.status ===
																									"Locação"
																								? "bg-purple-100 text-purple-800"
																								: "bg-gray-100 text-gray-800"
																				}`}
																			>
																				{property.attributes.status}
																			</span>
																		</div>
																		<div className="text-sm text-gray-600">
																			<div className="flex justify-between">
																				<span>
																					Quartos:{" "}
																					{property.attributes
																						.numberOfBedrooms || "N/A"}
																				</span>
																				{property.attributes.price && (
																					<span className="font-medium text-green-600">
																						{formatCurrency(
																							property.attributes.price,
																						)}
																					</span>
																				)}
																			</div>
																			{property.attributes.tagKanlo && (
																				<div className="mt-1">
																					<span className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-600">
																						Tag: {property.attributes.tagKanlo}
																					</span>
																				</div>
																			)}
																		</div>
																	</div>
																))}
															</div>
														) : (
															<p className="italic text-gray-500">
																Nenhum imóvel cadastrado neste bloco
															</p>
														)}
													</div>
												)}
											</div>
										))}
									</div>
								) : (
									<p className="italic text-gray-500">
										Nenhum bloco cadastrado
									</p>
								)}
							</div>

							{/* Informações Adicionais */}
							{(caretakerName || caretakerPhone) && (
								<div className="mt-4 border-t border-gray-100 pt-4">
									<h4 className="mb-2 font-medium text-gray-900">
										Informações do Zelador
									</h4>
									<div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
										{caretakerName && (
											<span className="flex items-center gap-1">
												<User size={16} className="text-gray-400" />
												{caretakerName}
											</span>
										)}
										{caretakerPhone && (
											<span className="flex items-center gap-1">
												<Phone size={16} className="text-gray-400" />
												{caretakerPhone}
											</span>
										)}
									</div>
								</div>
							)}
						</div>
					)}
				</div>

				<Toast.Root
					open={toastOpen}
					onOpenChange={setToastOpen}
					duration={5000}
					className={`fixed top-4 right-4 z-50 rounded-md p-4 shadow-lg ${
						toastType === "success"
							? "bg-green-500 text-white"
							: "bg-red-500 text-white"
					}`}
				>
					<Toast.Description>{toastMessage}</Toast.Description>
					<Toast.Close className="absolute right-1 top-1" />
				</Toast.Root>
				<Toast.Viewport />
			</Toast.Provider>
		</div>
	);
}
